// api/chat/chatQueries.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';

import { useChatViewerRole } from '../hooks/useChatViewerRole';
import { closeBulletin, decideBulletinResponse } from 'api/v1/requests/bulletins';

import {
  fetchChatRooms,
  fetchChatHistory,
  fetchChatContactShare,
  sendChatMessage,
  markChatRead,
  shareChatContacts,
  openChatFromResponse,
  type ChatRoomResponse,
  type ChatRoomStatus,
  type ChatViewerRole,
} from './routes';

// Ключи для кэширования
export const chatKeys = {
  all: ['chat'] as const,
  lists: (viewerRole?: ChatViewerRole, status?: ChatRoomStatus) =>
    [...chatKeys.all, 'list', viewerRole ?? 'all', status ?? 'all'] as const,
  details: () => [...chatKeys.all, 'detail'] as const,
  messages: (chatId: number) => [...chatKeys.details(), chatId, 'messages'] as const,
  contactShare: (chatId: number) => [...chatKeys.details(), chatId, 'contact-share'] as const,
};

// Хук для получения списка чатов
export const useChatRoomsQuery = (
  viewerRole: ChatViewerRole,
  status: ChatRoomStatus = 'active',
  enabled = true,
) => {

  return useQuery({
    queryKey: chatKeys.lists(viewerRole, status),
    queryFn: () => fetchChatRooms(viewerRole, status),
    staleTime: 15 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
    enabled,
  });
};

// Хук для получения истории чата (только сообщения)
export const useChatHistoryQuery = (chatId: number | null) => {
  return useQuery({
    queryKey: chatId ? chatKeys.messages(chatId) : ['chat', 'no-chat'],
    queryFn: () => (chatId ? fetchChatHistory(chatId) : []),
    enabled: !!chatId && chatId > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return false;
      }
      return failureCount < 1;
    },
  });
};

export const useChatContactShareQuery = (chatId: number | null) => {
  return useQuery({
    queryKey: chatId ? chatKeys.contactShare(chatId) : ['chat', 'no-contact-share'],
    queryFn: () => (chatId ? fetchChatContactShare(chatId) : null),
    enabled: !!chatId && chatId > 0,
    staleTime: 60_000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return false;
      }
      return failureCount < 1;
    },
  });
};

export const useSendChatMessageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      chatId,
      text,
      roomId,
    }: {
      chatId: number;
      text: string;
      roomId?: string | null;
    }) => sendChatMessage(chatId, text, roomId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
    },
  });
};

export const useMarkChatReadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => markChatRead(chatId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
    },
  });
};

export const useShareContactsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => shareChatContacts(chatId),
    onSuccess: (_data, chatId) => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.messages(chatId) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.contactShare(chatId) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });
};

export const useChatUnreadTotal = (): number => {
  const viewerRole = useChatViewerRole();
  const { data } = useChatRoomsQuery(viewerRole, 'active');
  return (data ?? []).reduce((sum, room) => sum + (room.unread_count ?? 0), 0);
};

/** Сколько чатов с непрочитанными (для бейджа пункта меню «Чаты» - реальное число чатов, а не сообщений). */
export const useChatUnreadRoomsCount = (): number => {
  const viewerRole = useChatViewerRole();
  const { data } = useChatRoomsQuery(viewerRole, 'active');
  return (data ?? []).filter((room) => (room.unread_count ?? 0) > 0).length;
};

export const invalidateChatLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
};

export const useOpenChatFromResponseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bulletinResponseId: number) => openChatFromResponse(bulletinResponseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
    },
  });
};

const refreshChatAfterDealChange = (
  queryClient: ReturnType<typeof useQueryClient>,
  chatId?: number | null,
) => {
  void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
  void queryClient.invalidateQueries({ queryKey: chatKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['student_chat_bulletin_threads'] });
  void queryClient.invalidateQueries({ queryKey: ['student_bulletin_tutors'] });
  void queryClient.invalidateQueries({ queryKey: ['my_responses'] });
  if (chatId) {
    void queryClient.invalidateQueries({ queryKey: chatKeys.contactShare(chatId) });
    void queryClient.invalidateQueries({ queryKey: chatKeys.messages(chatId) });
  }
};

const patchRelatedBulletinStatus = (
  queryClient: ReturnType<typeof useQueryClient>,
  responseId: number,
  nextStatus: string,
  extra?: { bulletinClosed?: boolean },
) => {
  const patchLists = (old: ChatRoomResponse[] | undefined) => {
    if (!old?.length) {
      return old;
    }
    return old.map((room) => {
      const bulletins = room.related_bulletins;
      if (!bulletins?.length) {
        return room;
      }
      let changed = false;
      const nextBulletins = bulletins.map((b) => {
        if (b.bulletin_response_id !== responseId) {
          return b;
        }
        changed = true;
        return {
          ...b,
          status: nextStatus,
          ...(extra?.bulletinClosed !== undefined
            ? { bulletin_closed: extra.bulletinClosed }
            : {}),
        };
      });
      return changed ? { ...room, related_bulletins: nextBulletins } : room;
    });
  };

  for (const role of ['student', 'tutor'] as const) {
    for (const status of ['active', 'closed'] as const) {
      queryClient.setQueryData<ChatRoomResponse[]>(
        chatKeys.lists(role, status),
        patchLists,
      );
    }
  }
  // Комната, открытая по ссылке ?chatId= (useDeepLinkedChatRoom), перекрывает список - патчим и её,
  // иначе кнопки «Принять/Отклонить» висят до перезагрузки.
  queryClient.setQueriesData<ChatRoomResponse | undefined>(
    { queryKey: [...chatKeys.all, 'room-by-id'] },
    (old) => (old ? patchLists([old])?.[0] : old),
  );
};

export const useDecideBulletinResponseMutation = (chatId?: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      responseId,
      action,
    }: {
      responseId: number;
      action: 'accept' | 'reject';
    }) => decideBulletinResponse(responseId, action),
    onMutate: async ({ responseId, action }) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.all });
      const nextStatus = action === 'accept' ? 'accepted' : 'rejected';
      patchRelatedBulletinStatus(queryClient, responseId, nextStatus);
      return { responseId, nextStatus };
    },
    onError: () => {
      refreshChatAfterDealChange(queryClient, chatId);
    },
    onSuccess: () => {
      refreshChatAfterDealChange(queryClient, chatId);
    },
  });
};

export const useCloseBulletinForDealMutation = (chatId?: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bulletinId: number) => closeBulletin(bulletinId),
    onMutate: async (bulletinId) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.all });
      const patchLists = (old: ChatRoomResponse[] | undefined) => {
        if (!old?.length) {
          return old;
        }
        return old.map((room) => {
          const bulletins = room.related_bulletins;
          if (!bulletins?.length) {
            return room;
          }
          let changed = false;
          const nextBulletins = bulletins.map((b) => {
            if (b.bulletin_id !== bulletinId) {
              return b;
            }
            changed = true;
            return { ...b, bulletin_closed: true };
          });
          return changed ? { ...room, related_bulletins: nextBulletins } : room;
        });
      };
      for (const role of ['student', 'tutor'] as const) {
        for (const status of ['active', 'closed'] as const) {
          queryClient.setQueryData<ChatRoomResponse[]>(
            chatKeys.lists(role, status),
            patchLists,
          );
        }
      }
      queryClient.setQueriesData<ChatRoomResponse | undefined>(
        { queryKey: [...chatKeys.all, 'room-by-id'] },
        (old) => (old ? patchLists([old])?.[0] : old),
      );
    },
    onError: () => {
      refreshChatAfterDealChange(queryClient, chatId);
    },
    onSuccess: () => {
      refreshChatAfterDealChange(queryClient, chatId);
    },
  });
};
